import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {GGGameConfig} from "../../GGGameConfig";
import {GGInitialWinCountUp} from "./GGInitialWinCountUp";
import {GGInitialWPView} from "./GGInitialWPView";
import {SequentiallyBetWayInitialWPController} from "@nolimitcity/slot-game/bin/game/winpresentation/initial/sequentially/SequentiallyBetWayInitialWPController";
import {Timeline, TimelineLite} from "gsap";
import {IBetWayWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {GGGameModuleConfig} from "../../GGGameModuleConfig";
import {GGParsedGameData} from "../../server/data/GGParsedGameData";
import {WaysWonGFXText} from "./WaysWonGFXText";
import {GGSoundConfig} from "../../GGSoundConfig";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {FontStyleConfig} from "../../config/FontStyleConfig";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {IXBomb} from "../../server/data/IGGServerData";
import {GGReelSymbolH} from "../../symbols/GGReelSymbolH";
import {GGReelView} from "../../reel/GGReelView";
import {GGReelController} from "../../reel/GGReelController";
import {GGReelSymbol} from "../../symbols/GGReelSymbol";
import {GGHelper} from "../../utils/Helper";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {BetWinMode} from "@nolimitcity/slot-game/bin/core/gametype/BetWinMode";
import {WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";

export class GGInitialWPController extends SequentiallyBetWayInitialWPController {
    private _gameView:GGInitialWPView;
    private _winCountUp:GGInitialWinCountUp;
    private _winNumberTimelines:(TimelineLite | null)[] = [];
    private _waysWon:WaysWonGFXText;
    private _winWaysWon:number = 0;
    private _animationTimeline:TimelineLite | null;
    private _animationAborted:boolean = false;

    private _winExplosionManySoundPlayed: boolean = false;

    constructor(indexes:[number, number]) {
        super(indexes, {
            name : "InitialWin",
            betWinMode : BetWinMode.BET_WAY,
            hasWinCountUp : false,
            skippable : true
        });

        this._gameView = new GGInitialWPView();
        this._winCountUp = new GGInitialWinCountUp(FontStyleConfig.CountUp);
        this._winCountUp.position.set(360, 415);
        StageManager.getLayer(GGGameConfig.instance.LAYERS.WIN_COUNT_UP.name).addChild(this._winCountUp);
        this._waysWon = new WaysWonGFXText();
    }

    protected startWinPresentation():void {
        this._winExplosionManySoundPlayed = false;

        const timeline:TimelineLite = new TimelineLite({paused : true});
        this._animationTimeline = new TimelineLite();
        const gameData:GGParsedGameData = (<GGParsedGameData>this._gameData!);
        if(gameData.brokeBank) {
            this.playMaxCapWinAnimation(this._animationTimeline);
        } else if(this._betWinsData != null && this._betWinsData.length > 0 || gameData.xBombs.bomb) {
            this.playWinAnimations(this._animationTimeline);
        } else {
            this.addWinCountUpToTimeline(this._animationTimeline);
        }

        timeline.add(() => this._animationTimeline!.play());
        Logger.logDev(`Initial animation timeline duration ${this._animationTimeline.totalDuration()}`);
        const finishTime:number = this._animationTimeline.totalDuration();
        timeline.add(() => this.finish(), finishTime);
        this._timeline = timeline;
        this._timeline.play(0);
    }

    protected playMaxCapWinAnimation(timeline:Timeline):void {
        const gameData:GGParsedGameData = (<GGParsedGameData>this._gameData!);
        let startTime:number = 0.1;

        for(let i:number = 0; i < this._betWinsData!.length; i++) {
            startTime += this.addSingleBetWinAnimationToTimeline(<IBetWayWinData>this._betWinsData![i], timeline, startTime);
        }

        const avalancheTime:number = timeline.totalDuration();
        timeline.add(() => SlotGame.winFieldController.showWinField(gameData.totalWin), avalancheTime);
        timeline.add(() => {}, avalancheTime + 1);
    }

    protected playWinAnimations(timeline:Timeline):void {
        const gameData:GGParsedGameData = (<GGParsedGameData>this._gameData!);
        const hasNormalWin:boolean = gameData.avalancheTotalWin > 0;
        const hasBomb:boolean = gameData.xBombs.bomb;

        let startTime:number = 0.1;
        let allWinPositions:boolean[][] = gameData.allWinPositions;

        if(hasNormalWin) {
            for(let i:number = 0; i < this._betWinsData!.length; i++) {
                startTime += this.addSingleBetWinAnimationToTimeline(<IBetWayWinData>this._betWinsData![i], timeline, startTime);
            }
            this.playSymbolExplosion(timeline!, allWinPositions, startTime);
            startTime = timeline.totalDuration();
        }

        let avalancheTime:number = startTime;

        if(hasBomb) {
            avalancheTime += 0.7;

            for(let i:number = 0; i < GGGameConfig.instance.REELS_NUM; i++) {
                (<GGReelController>Reels.getReel(i)).updateSize(gameData.gameAreaSizeNextSpin);
            }
            this.bombAnimationToTimeline(gameData.xBombs, timeline, startTime - 0.2);
            this.playBombedSymbolExplosion(timeline, gameData.xBombs.bombedArea, startTime - 0.2, false);
            timeline.add(() => GGGameModuleConfig.background.bombExplode(), avalancheTime - 0.2);
            timeline.add(() => {
                for(let i:number = 0; i < GGGameConfig.instance.REELS_NUM; i++) {
                    (<GGReelController>Reels.getReel(i)).resetSizeAfterBomb(gameData.gameAreaSizeNextSpin, gameData.xBombs.bombedArea[i]);
                }
            }, avalancheTime);
        }
        if(gameData.isNextAvalancheMode && (this._betWinsData != null && this._betWinsData.length > 0)) {
            timeline.add(() => SlotGame.winFieldController.showWinField(gameData.totalWin), avalancheTime);
        }

        const avalancheTimeline:TimelineLite = new TimelineLite({paused : true});

        //set bomb area to be empty for new symbols avalanche
        if(hasBomb) {
            gameData.xBombs.bombedArea.forEach((winPos:boolean[], reelId:number) => {
                winPos.forEach((hasWin:boolean, symId:number) => {
                    if(hasWin) {
                        allWinPositions[reelId][symId] = true;
                    }
                });
            });
        }

        allWinPositions.forEach((winPos:boolean[], reelId:number) => {
            if(winPos.includes(true)) {
                const avalanchePositions:boolean[] = winPos.concat();
                const numOffset:number = GGGameConfig.instance.SYMBOLS_NUM_IN_REEL[2] - gameData.gameAreaSize;
                for(let i:number = 0; i < numOffset; i++) {
                    avalanchePositions[i] = true;
                }
                Reels.addReelAvalancheToTimeline(reelId, avalancheTimeline, 0, false, avalanchePositions);
            }
        });
        timeline.add(() => avalancheTimeline.play(), avalancheTime);
    }

    protected getNextSymbolAnimationTimeOffset(duration:number):number {
        return 0;
    }

    protected addSingleBetWinAnimationToTimeline(betWayWinData:IBetWayWinData, timeline:Timeline, startTime:number):number {
        const delay:number = super.addSingleBetWinAnimationToTimeline(betWayWinData, timeline, startTime);

        // update betWays won
        this._winWaysWon += betWayWinData.numberOfCombinations;
        const winWaysWon = this._winWaysWon;
        timeline.add(() => {
            this._waysWon.showWinWaysWon(winWaysWon);
        }, startTime);
        return delay + 0.2;
    }

    private bombAnimationToTimeline(bombData:IXBomb, timeline:Timeline, startTime:number):number {
        let delay:number = 0;
        bombData.bombsPositions.forEach((bombOnReel:boolean[], i:number) => {
            bombOnReel.forEach((hasBomb:boolean, j:number) => {
                if(hasBomb) {
                    const bomb:GGReelSymbolH = <GGReelSymbolH>this.findSymbol(i, j);

                    timeline.add([
                        bomb.changeState({
                            state : GGGameConfig.instance.SYMBOL_STATES.explode
                        }),
                        () => {
                            (<GGReelView>Reels.getReel(i)!.view).addSymbolToWinContainer(bomb, true);
                            GGGameModuleConfig.soundPlayer.playSoundEffect(GGSoundConfig.BOMB_EXPLODE);
                        }
                    ], startTime + delay);
                    delay += 0.1;
                }
            })
        });
        return 0;
    }

    protected addSymbolWinAnimationToTimeline(betWayWinData:IBetWayWinData, timeline:Timeline, startTime:number, symbol:StateReelSymbol, reelId:number, symbolId:number):number {
        const position:[number, number] = Helper.getSymbolPositions(reelId, symbolId, true);
        if(symbol.symName.length>2){
            position[1] = position[1] - 15;
        }
        timeline.add(() => this.sortSymbolDepthToTop(symbol), startTime);
        // only play on the last reel top symbol.
        if((reelId === betWayWinData.length - 1) && betWayWinData.reelWinPositions[reelId].indexOf(true) === symbolId) {
            // The win number animation will not be removed by abort and stop.
            const winNumberTimeline:TimelineLite = this._gameView.playWinNumbers(betWayWinData, position);
            this._winNumberTimelines.push(winNumberTimeline);
            timeline.add(() => winNumberTimeline.play(), startTime);
            (<TimelineLite>timeline).removeLabel("displayed"); // To remove if that's not the last one.
            (<TimelineLite>timeline).addLabel("displayed", startTime);
        }
        return 0;
    }

    protected addWinCountUpToTimeline(timeline:Timeline):void {
        const gameData: GGParsedGameData = <GGParsedGameData>this._gameData!;
        if (gameData.isEndBigWin) {
            // leave the ways won move down and remove itself.
            return;
        }
        const duration: number = gameData.currentAvalancheWinRatioConfig!.duration,
            countUpTimeline: TimelineLite = new TimelineLite(),
            // #447
            avalancheTotalWinBelowStake: boolean = (SlotGame.api.gameClientConfiguration.belowStakeWinRestriction && (gameData.avalancheTotalWin <= gameData.playedBetValue));

        countUpTimeline.add(() => {
            switch (gameData.currentAvalancheWinRatioConfig!.type) {
                case WinRatioType.TINY:
                    this._winCountUp.scale.set(0.6);
                    break;
                case WinRatioType.SMALL:
                    this._winCountUp.scale.set(0.8);
                    break;
                default:
                    this._winCountUp.scale.set(1);
                    break;
            }
            this._winCountUp.getCountUpAnimation(0, gameData.avalancheTotalWin, duration);
        }, 0.2);

        // #447
        if (!avalancheTotalWinBelowStake) {
            countUpTimeline.add(() => GGGameModuleConfig.soundPlayer.playNormalWinSound(gameData.currentAvalancheWinRatioConfig!), 0.2 + duration);
        }
        timeline.add(countUpTimeline, "displayed+=0.1");
    }

    protected hasWin():boolean {
        // #576
        if (GGGameConfig.instance.FAULTY_WIN_CAP) {
            return false;
        }

        const gameData:GGParsedGameData = (<GGParsedGameData>this._gameData!);
        return (gameData.avalancheTotalWin > 0 || gameData.xBombs.bomb);
    }

    private playSymbolExplosionOnAbort(allWinPositions:boolean[][]):TimelineLite {
        const explosions:TimelineSprite[] = [];
        const explosionTl:TimelineLite = new TimelineLite();
        explosionTl.add(()=> {
            // #558, #557 => checking if there is any win/symbols exploding, it is sufficient to check 1st reel only, since win always starts from 1st reel.
            if (allWinPositions[0].includes(true) && !this._winExplosionManySoundPlayed) {
                GGGameModuleConfig.soundPlayer.stopWinSoundsOnAbort();

                this._winExplosionManySoundPlayed = true;
            }
        });
        allWinPositions.forEach((winPos:boolean[], reelId:number) => {
            winPos.forEach((hasWin:boolean, symId:number) => {
                if(hasWin) {
                    const sym:StateReelSymbol = this.findSymbol(reelId, symId)!;
                    const explosion = GGHelper.createTimelineSprite("symExplosion");
                    explosion.blendMode = PIXI.BLEND_MODES.ADD;
                    explosions.push(explosion)
                    StageManager.getLayer(GGGameConfig.instance.LAYERS.GOBLINS.name).addChild(explosion);
                    explosion.position = GGHelper.copyLocalPosition(explosion, sym);
                    explosionTl.add(explosion.getAnimationAutoShowHide(true, true), 0);
                }
            });
        });
        explosionTl.add(() => {
            explosions.forEach((animation:TimelineSprite) => {
                animation.destroy();
            });
        });
        return explosionTl;
    }

    private playSymbolExplosion(timeline:Timeline, allWinPositions:boolean[][], startTime:number):void {
        const allTogether:boolean = this._gameData!.currentWinRatioConfig!.duration < 1;
        let hasM:boolean = false;
        const highLightTl:TimelineLite = new TimelineLite();
        const explosionTl:TimelineLite = new TimelineLite();
        const randomStart:number = allTogether ? 0 : Math.floor(Math.random() * 5);
        allWinPositions.forEach((winPos:boolean[], reelId:number) => {
            let symbolExplosionTime:number = (allTogether ? 0 : (reelId * 0.1));
            let hasWinOnReel:boolean = false;
            winPos.forEach((hasWin:boolean, symId:number) => {
                if(hasWin) {
                    const sym:StateReelSymbol = this.findSymbol(reelId, symId)!;
                    if(sym.symName[0] === "M" || sym.symName[0] === "W") {
                        hasM = true;
                    }
                    hasWinOnReel = true;

                    highLightTl.add((<GGReelSymbol>sym).playWinAnimation(), 0);
                    explosionTl.add([
                        () => (<GGReelView>Reels.getReel(reelId)!.view).addSymbolToWinContainer(sym, true),
                        sym.changeState({
                            state : GGGameConfig.instance.SYMBOL_STATES.explode
                        })
                    ], 0.1 + symbolExplosionTime);
                }
            });
            if(hasWinOnReel && !allTogether) {
                explosionTl.add(() => GGGameModuleConfig.soundPlayer.playLargerWinExplosionSound(randomStart, reelId), symbolExplosionTime + 0.8);
            }
        });
        if(allTogether) {
            explosionTl.add(() => {
                if (!this._winExplosionManySoundPlayed) {
                    GGGameModuleConfig.soundPlayer.playSmallWinExplosionSound();

                    this._winExplosionManySoundPlayed = true;
                }
            }, 0.8);
        }
        if(hasM) {
            explosionTl.add(() => GGGameModuleConfig.soundPlayer.playWinRowMidSym(), 0);
        }
        highLightTl.add(() => GGGameModuleConfig.soundPlayer.playBetlineWin(), 0);
        timeline.add(explosionTl, startTime);
        timeline.add(highLightTl, startTime + 0.5);
    }

    private playBombedSymbolExplosion(timeline:Timeline, bombedArea:boolean[][], startTime:number, wins:boolean):void {
        const wonPos = (<GGParsedGameData>this._gameData!).allWinPositions;
        const bombPos = (<GGParsedGameData>this._gameData!).xBombs.bombsPositions;
        bombedArea.forEach((winPos:boolean[], reelId:number) => {
            let symbolExplosionTime:number = startTime + 0.1;
            winPos.forEach((hasWin:boolean, symId:number) => {
                if(hasWin && !wonPos[reelId][symId] && !bombPos[reelId][symId]) {
                    const sym:StateReelSymbol = this.findSymbol(reelId, symId)!;
                    const tl:TimelineLite = new TimelineLite();

                    tl.add([
                        () => {
                            (<GGReelView>Reels.getReel(reelId)!.view).addSymbolToWinContainer(sym, true);
                        },
                        sym.changeState({state : GGGameConfig.instance.SYMBOL_STATES.explode})
                    ]);
                    timeline.add(tl, symbolExplosionTime);
                }
            });
        });
    }

    protected stopWinPresentation(isAborted:boolean):void {
        if(this._timeline) {
            if(this._timeline.isActive()) {
                this._timeline.pause();
                this._timeline.kill();
            }
            this._timeline = null;
        }
        if(this._animationTimeline) {
            // Logger.logDev(`initial win animation timeline progress ${this._animationTimeline.progress()}`);
            this._animationAborted = true;
            const hasNormalWin:boolean = (<GGParsedGameData>this._gameData!).avalancheTotalWin > 0;
            if(hasNormalWin && this._animationTimeline.progress() <= 0.7) {
                this.playSymbolExplosionOnAbort((<GGParsedGameData>this._gameData!).allWinPositions);
            }
            if(this._animationTimeline.progress() === 0) {
                this._animationTimeline.play();
                this._animationTimeline.progress(1);
                this._animationTimeline.kill();
            } else if(this._animationTimeline.isActive()) {
                // Logger.logDev(`initial win animation timeline aborted!`);
                this._animationTimeline.progress(1);
                this._animationTimeline.kill();
            }
            this._animationTimeline = null;
        }
        this._winSymbols = [];
        // Do no abort the avalanche timeline so it can be continued.
        const gameData:GGParsedGameData = (<GGParsedGameData>this._gameData!);
        if(!gameData.isNextAvalancheMode && gameData.isEndBigWin) {
            return;
        }
        if(!((this._betWinsData != null && this._betWinsData.length > 0) || gameData.xBombs.bomb)) {
            this._winCountUp.abort(gameData.avalancheTotalWin, isAborted, this._gameData!.isBigWin);
        } else {
            this._waysWon.abort(isAborted, false);
        }
    }

    public resetFeature():void {
        this._winWaysWon = 0;
        // Only abort/stop win numbers when skip/spinStart/ScreenStart
        while(this._winNumberTimelines.length > 0) {
            let timeline:TimelineLite | null = this._winNumberTimelines.pop()!;
            if(timeline.progress() === 0) {
                timeline.play();
                timeline.progress(1);
                timeline.kill();
            } else if(timeline.isActive()) {
                // Logger.logDev(`initial win animation timeline aborted!`);
                timeline.progress(1);
                timeline.kill();
            }
            timeline = null;
        }
        this._animationAborted = false;
    }

    protected findSymbol(reelId:number, symId:number):StateReelSymbol | null {
        if(Reels.getSymbol(reelId, symId) === null) {
            debugger;
            throw new Error(`Error: SequentiallyBetWayInitialWPController.playBetWinPresentation(): Cannot find symbol at reel ${reelId} symbol ${symId}`);
        }
        return Reels.getSymbol(reelId, symId);
    }

    // #574
    public play(): void {
        this._skippable = (this._gameData!.nextMode.indexOf("AVALANCHE") > -1) || (!(this._gameData! as GGParsedGameData).isEndBigWin);

        super.play();
    }
}